import { useEffect, useMemo, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  BarChart3, ChevronLeft, ChevronRight, Clock, AlertTriangle, LayoutDashboard, FileBarChart, Pencil,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import VenturaLogo from "@/components/VenturaLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { sectionGradientStyle, sectionIconInactiveStyle } from "@/lib/sectionTheme";


function fmtMin(min) {
  if (min == null) return "—";
  const abs = Math.abs(Math.round(min));
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const signo = min < 0 ? "−" : "";
  return m ? `${signo}${h} h ${String(m).padStart(2, "0")} min` : `${signo}${h} h`;
}

function totalMin(worker) {
  return worker.minutos.reduce((acc, n) => acc + (n || 0), 0);
}

function estado(fichadas, objetivo, enCurso) {
  const diff = fichadas - objetivo;
  if (enCurso && diff <= 30) {
    return { clave: "curso", label: "Semana en curso", diff };
  }
  if (Math.abs(diff) <= 30) {
    return { clave: "ok", label: "En objetivo", diff };
  }
  if (diff > 0) {
    return { clave: "sobre", label: "Por encima del horario", diff };
  }
  return { clave: "bajo", label: "Por debajo del horario", diff };
}

function colorBarra(clave) {
  if (clave === "sobre") return "#d97706";
  if (clave === "bajo") return "#0284c7";
  if (clave === "curso") return "#0f766e";
  return "#0f766e";
}

function TooltipGrafico({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs shadow-md">
      <p className="font-semibold text-zinc-900">{d.nombre}</p>
      <p className="mt-1 text-zinc-600">Fichadas: <strong>{fmtMin(d.fichadasMin)}</strong></p>
      <p className="text-zinc-600">Según horario: <strong>{fmtMin(d.objetivoMin)}</strong></p>
      <p className="text-zinc-600">Diferencia: <strong>{fmtMin(d.diff)}</strong></p>
    </div>
  );
}

const TIPOS_PICADO = ["entrada", "pausa", "vuelta", "salida"];

function fechaCorta(semanaId, diaIndex) {
  const d = new Date(`${semanaId}T12:00:00`);
  d.setDate(d.getDate() + diaIndex);
  return d.toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" });
}

function lunesISO(base = new Date()) {
  const d = new Date(base);
  d.setHours(12, 0, 0, 0);
  const offset = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - offset);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function sumarDiasISO(iso, dias) {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + dias);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

export function HorasSemanaVista() {
  const lunesHoy = lunesISO();
  const [semanaId, setSemanaId] = useState(lunesHoy);
  const [semana, setSemana] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [centro, setCentro] = useState("todos");
  const [abierto, setAbierto] = useState(null);
  const [form, setForm] = useState(null);
  const [ajustes, setAjustes] = useState([]);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    let vivo = true;
    setCargando(true);
    setError("");
    api.get("/informes/horas-semana", { params: { semana: semanaId } })
      .then(({ data }) => {
        if (!vivo) return;
        setSemana(data);
        setAbierto((actual) => actual ?? data.trabajadores?.[0]?.id ?? null);
      })
      .catch((err) => {
        if (vivo) setError(formatApiError(err));
      })
      .finally(() => {
        if (vivo) setCargando(false);
      });
    return () => {
      vivo = false;
    };
  }, [semanaId]);

  const centros = useMemo(() => {
    const set = new Set((semana?.trabajadores || []).map((t) => t.centro).filter(Boolean));
    return ["todos", ...set];
  }, [semana]);

  const lista = useMemo(() => {
    return (semana?.trabajadores || [])
      .filter((t) => centro === "todos" || t.centro === centro)
      .map((t) => {
        const fichadasMin = totalMin(t);
        const objetivo = t.objetivo_minutos || 0;
        const est = estado(fichadasMin, objetivo, semana.en_curso);
        return { ...t, fichadasMin, objetivoMin: objetivo, ...est };
      });
  }, [semana, centro]);

  const grafico = lista.map((t) => ({
    id: t.id,
    nombre: t.nombre.split(" ").slice(0, 2).join(" "),
    fichadas: Math.round((t.fichadasMin / 60) * 10) / 10,
    horario: Math.round(((t.objetivoMin || 0) / 60) * 10) / 10,
    fichadasMin: t.fichadasMin,
    objetivoMin: t.objetivoMin,
    diff: t.diff,
    clave: t.clave,
  }));
  const ejeMax = Math.max(8, ...grafico.flatMap((g) => [g.fichadas, g.horario])) + 2;

  const resumen = {
    n: lista.length,
    sobre: lista.filter((t) => t.clave === "sobre").length,
    bajo: lista.filter((t) => t.clave === "bajo").length,
    ok: lista.filter((t) => t.clave === "ok").length,
    curso: lista.filter((t) => t.clave === "curso").length,
  };

  const maxDia = Math.max(8 * 60, ...lista.flatMap((t) => (t.minutos || []).filter((n) => n != null)));

  const abrirAjuste = (workerId, diaIndex) => {
    const t = (semana?.trabajadores || []).find((w) => w.id === workerId);
    if (!t) return;
    let dia = diaIndex;
    if (dia == null) {
      dia = t.minutos.findIndex((m) => m != null);
      if (dia < 0) dia = 0;
    }
    const eventos = t.eventos?.[dia] || [];
    const sinPicados = eventos.length === 0;
    const primero = eventos[0];
    setAbierto(workerId);
    setForm({
      workerId,
      diaIndex: dia,
      motivo: sinPicados ? "falta" : "regularizar",
      tipo: primero?.tipo || (sinPicados ? "entrada" : "salida"),
      fichajeId: primero?.id || "",
      hora: "",
      nota: "",
      error: "",
    });
  };

  const enviarAjuste = async () => {
    if (!form.hora) {
      setForm({ ...form, error: "Indica la hora que debe quedar." });
      return;
    }
    if ((form.nota || "").trim().length < 3) {
      setForm({ ...form, error: "Escribe una nota para el trabajador (mínimo 3 caracteres)." });
      return;
    }
    const t = semana.trabajadores.find((w) => w.id === form.workerId);
    const fecha = semana.fechas[form.diaIndex];
    const nota = form.nota.trim();
    setEnviando(true);
    try {
      const puedeModificar = form.motivo === "regularizar"
        && form.fichajeId
        && (form.tipo === "entrada" || form.tipo === "salida");
      if (puedeModificar) {
        await api.post(`/fichajes/${form.fichajeId}/solicitar-modificacion`, {
          timestamp: `${fecha}T${form.hora}:00`,
          tipo: form.tipo,
          motivo: nota,
        });
      } else {
        await api.post("/fichajes/recordatorios", {
          usuario_ids: [t.id],
          mensaje: `${fechaCorta(semana.semana_inicio, form.diaIndex)}: ${form.motivo === "falta" ? "falta" : "regularizar"} ${form.tipo} a las ${form.hora}. ${nota}`,
          enviar_email: true,
        });
      }
      const nuevo = {
        id: `${semana.semana_inicio}-${form.workerId}-${form.diaIndex}-${form.tipo}-${form.hora}`,
        semanaId: semana.semana_inicio,
        workerId: form.workerId,
        nombre: t.nombre,
        diaIndex: form.diaIndex,
        dia: fechaCorta(semana.semana_inicio, form.diaIndex),
        motivo: form.motivo,
        tipo: form.tipo,
        hora: form.hora,
        nota,
      };
      setAjustes((prev) => [nuevo, ...prev.filter((a) => a.id !== nuevo.id)]);
      setForm(null);
      toast.success(puedeModificar ? "Ajuste enviado al trabajador para que lo confirme" : "Aviso enviado al trabajador");
    } catch (err) {
      setForm({ ...form, error: formatApiError(err) });
    } finally {
      setEnviando(false);
    }
  };

  const ajustesSemana = ajustes.filter((a) => a.semanaId === semana?.semana_inicio);
  const formTrabajador = form ? (semana?.trabajadores || []).find((w) => w.id === form.workerId) : null;
  const eventosDia = formTrabajador?.eventos?.[form?.diaIndex] || [];

  return (
    <div className="space-y-5" data-testid="horas-semana-preview">
      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">{error}</div>
      )}

      <PageHeader
        sectionKey="horas"
        icon={BarChart3}
        title="Horas semanales"
        subtitle={semana?.empresa
          ? `${semana.empresa}: horas fichadas frente al horario de cada trabajador`
          : "Horas fichadas frente al horario de cada trabajador"}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Semana anterior"
            onClick={() => setSemanaId((id) => sumarDiasISO(id, -7))}
            className="rounded-lg border border-zinc-200 bg-white p-2 text-zinc-600"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div>
            <p className="text-sm font-semibold text-zinc-900">{semana?.titulo || "Cargando semana…"}</p>
            <p className="text-xs text-zinc-500">
              {cargando ? "Leyendo picados de la empresa" : semana?.en_curso ? "Semana en curso" : "Semana cerrada"}
            </p>
          </div>
          <button
            type="button"
            aria-label="Semana siguiente"
            disabled={semanaId >= lunesHoy}
            onClick={() => setSemanaId((id) => sumarDiasISO(id, 7))}
            className="rounded-lg border border-zinc-200 bg-white p-2 text-zinc-600 disabled:opacity-40"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {centros.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCentro(c)}
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                centro === c ? "bg-teal-700 text-white" : "bg-white text-zinc-600 ring-1 ring-zinc-200"
              }`}
            >
              {c === "todos" ? "Todos los centros" : c}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Resumen etiqueta="Trabajadores" valor={resumen.n} />
        <Resumen etiqueta={semana?.en_curso ? "En curso" : "En objetivo"} valor={semana?.en_curso ? resumen.curso : resumen.ok} tono={semana?.en_curso ? undefined : "ok"} />
        <Resumen etiqueta="Por encima" valor={resumen.sobre} tono="sobre" />
        <Resumen etiqueta="Por debajo" valor={resumen.bajo} tono="bajo" />
      </div>

      {cargando && <p className="text-sm text-zinc-500">Cargando los trabajadores de la empresa…</p>}
      {!cargando && !error && lista.length === 0 && (
        <p className="rounded-xl border border-zinc-200 bg-white px-4 py-6 text-sm text-zinc-500">
          No hay trabajadores activos en tu empresa para esta semana.
        </p>
      )}

      {lista.length > 0 && (
      <section className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900">Comparación de la semana</h2>
            <p className="text-xs text-zinc-500">Cada persona, horas fichadas y objetivo del horario</p>
          </div>
          <div className="flex gap-3 text-[11px] text-zinc-500">
            <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-teal-700" /> En objetivo</span>
            <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-sky-600" /> Por debajo</span>
            <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-amber-600" /> Por encima</span>
            <span className="inline-flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-zinc-200" /> Horario</span>
          </div>
        </div>
        <div className="w-full" style={{ height: Math.max(280, lista.length * 36) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={grafico} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" horizontal={false} />
              <XAxis type="number" domain={[0, ejeMax]} tick={{ fontSize: 11 }} unit=" h" />
              <YAxis type="category" dataKey="nombre" width={128} tick={{ fontSize: 12 }} />
              <Tooltip content={<TooltipGrafico />} />
              <Bar dataKey="fichadas" name="Fichadas" radius={[0, 6, 6, 0]} barSize={14}>
                {grafico.map((r) => (
                  <Cell key={r.id} fill={colorBarra(r.clave)} />
                ))}
              </Bar>
              <Bar dataKey="horario" name="Según horario" fill="#e4e4e7" radius={[0, 6, 6, 0]} barSize={14} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>
      )}

      <div className="space-y-3">
        {lista.map((t) => {
          const abiertoEste = abierto === t.id;
          return (
            <article key={t.id} className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center gap-4 px-4 py-4">
                <button
                  type="button"
                  onClick={() => setAbierto(abiertoEste ? null : t.id)}
                  className="flex min-w-0 flex-1 flex-wrap items-center gap-4 text-left"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-zinc-900">{t.nombre}</p>
                    <p className="text-xs text-zinc-500">{t.centro} · {t.horario_label}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <Dato label="Fichadas" valor={fmtMin(t.fichadasMin)} />
                    <Dato label="Horario" valor={fmtMin(t.objetivoMin)} />
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${tonoClase(t.clave)}`}>
                      {t.clave === "curso"
                        ? `${fmtMin(t.fichadasMin)} de ${fmtMin(t.objetivoMin)}`
                        : `${t.diff > 0 ? "+" : ""}${fmtMin(t.diff)}`}
                    </span>
                  </div>
                </button>
                <Button type="button" size="sm" variant="outline" onClick={() => abrirAjuste(t.id, null)}>
                  <Pencil className="h-3.5 w-3.5" />
                  Pedir ajuste
                </Button>
              </div>
              {t.aviso && (
                <p className="mx-4 mb-3 flex gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-950">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {t.aviso}
                </p>
              )}
              {abiertoEste && (
                <div className="grid grid-cols-2 gap-2 border-t border-zinc-100 p-4 sm:grid-cols-4 lg:grid-cols-7">
                  {(semana?.dias || []).map((dia, i) => {
                    const min = t.minutos[i];
                    const futuro = min == null;
                    const alto = futuro ? 0 : Math.max(4, Math.round(((min || 0) / maxDia) * 72));
                    return (
                      <div key={dia} className="rounded-xl bg-zinc-50 p-2">
                        <p className="text-[11px] font-semibold text-zinc-500">{dia}</p>
                        <p className="mt-1 text-sm font-semibold text-zinc-900">{futuro ? "—" : fmtMin(min || 0)}</p>
                        <div className="mt-2 flex h-[72px] items-end">
                          <div
                            className="w-full rounded-md"
                            style={{
                              height: futuro ? 4 : alto,
                              background: futuro ? "#e4e4e7" : colorBarra(t.clave),
                              opacity: min ? 1 : 0.35,
                            }}
                          />
                        </div>
                        <p className="mt-2 text-[10px] leading-snug text-zinc-500">{t.picados[i]}</p>
                        {!futuro && (
                          <button
                            type="button"
                            onClick={() => abrirAjuste(t.id, i)}
                            className="mt-2 text-[11px] font-medium text-teal-800 underline-offset-2 hover:underline"
                          >
                            {ajustesSemana.some((a) => a.workerId === t.id && a.diaIndex === i) ? "Otro ajuste" : "Pedir ajuste"}
                          </button>
                        )}
                        {ajustesSemana
                          .filter((a) => a.workerId === t.id && a.diaIndex === i)
                          .map((a) => (
                            <p key={a.id} className="mt-1 rounded-md bg-teal-50 px-1.5 py-1 text-[10px] text-teal-900">
                              Pedido: {a.motivo === "falta" ? "falta" : "regularizar"} {a.tipo} a las {a.hora}
                            </p>
                          ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </article>
          );
        })}
      </div>

      {ajustesSemana.length > 0 && (
        <section className="rounded-2xl border border-teal-200 bg-white p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-zinc-900">Ajustes pedidos al trabajador</h2>
          <p className="mt-1 text-xs text-zinc-500">
            En la versión implantada, cada petición llega al trabajador para que regularice el picado.
          </p>
          <ul className="mt-3 space-y-2">
            {ajustesSemana.map((a) => (
              <li key={a.id} className="rounded-xl bg-teal-50 px-3 py-2 text-sm text-teal-950">
                <span className="font-medium">{a.nombre}</span>
                {" · "}
                {a.dia}
                {" · "}
                {a.motivo === "falta" ? "Falta" : "Regularizar"} {a.tipo} a las {a.hora}
                <span className="mt-0.5 block text-xs text-teal-800">{a.nota}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Dialog open={!!form} onOpenChange={(open) => { if (!open) setForm(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Pedir ajuste de picado</DialogTitle>
          </DialogHeader>
          {form && formTrabajador && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-600">
                Se pide a <strong>{formTrabajador.nombre}</strong> que deje el picado como indicas.
                El día muestra ahora: {formTrabajador.picados[form.diaIndex]}
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="ajuste-dia">Día</Label>
                <select
                  id="ajuste-dia"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                  value={String(form.diaIndex)}
                  onChange={(e) => {
                    const diaIndex = Number(e.target.value);
                    const ev = formTrabajador.eventos?.[diaIndex] || [];
                    const sin = ev.length === 0;
                    setForm({
                      ...form,
                      diaIndex,
                      motivo: sin ? "falta" : form.motivo,
                      fichajeId: ev[0]?.id || "",
                      tipo: ev[0]?.tipo || form.tipo,
                      error: "",
                    });
                  }}
                >
                  {(semana?.dias || []).map((dia, i) => (
                    formTrabajador.minutos[i] == null ? null : (
                      <option key={dia} value={i}>{semana.fechas?.[i] ? fechaCorta(semana.semana_inicio, i) : dia} · {dia}</option>
                    )
                  ))}
                </select>
              </div>
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">Motivo</legend>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="motivo-ajuste"
                    checked={form.motivo === "falta"}
                    onChange={() => setForm({ ...form, motivo: "falta", error: "" })}
                  />
                  Falta un picado
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="motivo-ajuste"
                    checked={form.motivo === "regularizar"}
                    onChange={() => setForm({ ...form, motivo: "regularizar", error: "" })}
                  />
                  Hay que regularizar un picado que ya existe
                </label>
              </fieldset>
              {form.motivo === "regularizar" && eventosDia.length > 0 && (
                <div className="space-y-1.5">
                  <Label htmlFor="ajuste-picado">Picado que hay que regularizar</Label>
                  <select
                    id="ajuste-picado"
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                    value={String(form.fichajeId || "")}
                    onChange={(e) => {
                      const ev = eventosDia.find((item) => String(item.id) === e.target.value);
                      setForm({
                        ...form,
                        fichajeId: ev?.id || "",
                        tipo: ev?.tipo || form.tipo,
                        error: "",
                      });
                    }}
                  >
                    {eventosDia.map((ev) => (
                      <option key={ev.id} value={ev.id}>{ev.hora} {ev.tipo}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="ajuste-tipo">Picado</Label>
                  <select
                    id="ajuste-tipo"
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm capitalize"
                    value={form.tipo}
                    onChange={(e) => setForm({ ...form, tipo: e.target.value, error: "" })}
                  >
                    {TIPOS_PICADO.map((tipo) => (
                      <option key={tipo} value={tipo}>{tipo}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ajuste-hora">Hora que debe quedar</Label>
                  <Input
                    id="ajuste-hora"
                    type="time"
                    value={form.hora}
                    onChange={(e) => setForm({ ...form, hora: e.target.value, error: "" })}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ajuste-nota">Nota para el trabajador</Label>
                <Textarea
                  id="ajuste-nota"
                  rows={3}
                  value={form.nota}
                  placeholder="Qué tiene que corregir y por qué"
                  onChange={(e) => setForm({ ...form, nota: e.target.value, error: "" })}
                />
              </div>
              {form.error && <p className="text-sm text-rose-600">{form.error}</p>}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setForm(null)}>Cancelar</Button>
            <Button type="button" onClick={enviarAjuste} disabled={enviando}>
              {enviando ? "Enviando…" : "Enviar al trabajador"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Resumen({ etiqueta, valor, tono }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 shadow-sm">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">{etiqueta}</p>
      <p className={`mt-1 text-2xl font-semibold ${tono === "sobre" ? "text-amber-700" : tono === "bajo" ? "text-sky-700" : tono === "ok" ? "text-teal-800" : "text-zinc-900"}`}>
        {valor}
      </p>
    </div>
  );
}

function Dato({ label, valor }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wide text-zinc-400">{label}</p>
      <p className="font-medium text-zinc-800">{valor}</p>
    </div>
  );
}

function tonoClase(clave) {
  if (clave === "sobre") return "bg-amber-100 text-amber-900";
  if (clave === "bajo") return "bg-sky-100 text-sky-900";
  if (clave === "curso") return "bg-zinc-100 text-zinc-700";
  return "bg-teal-100 text-teal-900";
}

const MENU = [
  { label: "Panel", icon: LayoutDashboard, key: "panel" },
  { label: "Fichajes", icon: Clock, key: "fichajes" },
  { label: "Listados e informes", icon: FileBarChart, key: "informes" },
  { label: "Horas semanales", icon: BarChart3, key: "horas", activo: true },
];

export default function EjemploHorasSemana() {
  return (
    <div className="min-h-screen bg-zinc-50">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-indigo-100/80 bg-white md:block">
        <div className="border-b border-indigo-100/80 p-4">
          <VenturaLogo className="h-8 w-auto" />
          <p className="mt-2 px-1 text-[10px] font-semibold uppercase tracking-wider text-indigo-400">
            Administrador
          </p>
        </div>
        <nav className="space-y-1 px-3 py-4">
          {MENU.map(({ label, icon: Icon, key, activo }) => (
            <div
              key={label}
              className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm ${activo ? "font-medium text-white shadow-md" : "text-zinc-600"}`}
              style={activo ? sectionGradientStyle(key) : undefined}
            >
              <span
                className={`flex h-8 w-8 items-center justify-center rounded-lg ${activo ? "bg-white/20" : ""}`}
                style={!activo ? sectionIconInactiveStyle(key) : undefined}
              >
                <Icon className={`h-4 w-4 ${activo ? "text-white" : ""}`} strokeWidth={1.5} />
              </span>
              {label}
            </div>
          ))}
        </nav>
      </aside>
      <main className="md:pl-64">
        <div className="mx-auto max-w-6xl p-4 md:p-8">
          <HorasSemanaVista />
        </div>
      </main>
    </div>
  );
}
